<!-- TheMenu.vue -->
<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import jaJP from "@/locales/ja-JP.json";
import enUS from "@/locales/en-US.json";
import { mdiHomeOutline } from "@mdi/js";
import { mdiAccountVoice } from '@mdi/js';
import { mdiHelpCircleOutline } from '@mdi/js';
import { mdiInformationOutline } from '@mdi/js';

type MessageSchema = typeof jaJP;

const { t } = useI18n<{
  message: MessageSchema
}, "ja-JP" | "en-US">({
  inheritLocale: true,
  messages: {
    "ja-JP": jaJP,
    "en-US": enUS,
  }
});

const menuOpen = ref(false);
const userMenuOpen = ref(false);

const closeMenu = () => {
  userMenuOpen.value = false;
  menuOpen.value = false;
};

</script>

<template>
  <!--
    css hamburger menu
    https://www.asobou.co.jp/blog/web/css-menu
  -->
  <div class="menu">
    <div class="menu-btn" :class="{ show: menuOpen }" @click="menuOpen = !menuOpen">
      <span />
    </div>
    <div class="menu-content-back" @click="closeMenu" />
    <nav class="menu-content">
      <ul class="menu-list">
        <li>
          <router-link to="/" @click="closeMenu">
            <svg class="menu-icon" viewBox="0 0 24 24">
              <path fill="#fff" :d="mdiHomeOutline" />
            </svg>{{ t("menu.home") }}
          </router-link>
        </li>
        <li>
          <router-link to="/voiceList" @click="closeMenu">
            <svg class="menu-icon" viewBox="0 0 24 24">
              <path fill="#fff" :d="mdiAccountVoice" />
            </svg>{{ t("menu.voiceList") }}
          </router-link>
        </li>
        <li>
          <router-link to="/help" @click="closeMenu">
            <svg class="menu-icon" viewBox="0 0 24 24">
              <path fill="#fff" :d="mdiHelpCircleOutline" />
            </svg>{{ t("menu.help") }}
          </router-link>
        </li>
        <li>
          <router-link to="/webmaster" @click="closeMenu">
            <svg class="menu-icon" viewBox="0 0 24 24">
              <path fill="#fff" :d="mdiInformationOutline" />
            </svg>{{ t("menu.webmaster") }}
          </router-link>
        </li>
      </ul>
    </nav>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/sass/mixin.scss";

.menu-content {
  padding: 0;
}

.menu-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.menu-btn {
  position: fixed;
  top: 2px;
  left: 2px;
  display: flex;
  height: 3rem;
  width: 3rem;
  border: 1px solid #22405f;
  transition: border-color 1s;
  background-color: #22405f;
  justify-content: center;
  align-items: center;
  z-index: 32;
  &:hover {
    border: 1px solid #37ffff;
    transition: border-color 1s;
  }
  span,
  span::before,
  span::after {
    content: "";
    display: block;
    height: 3px;
    width: 1.5rem;
    border-radius: 3px;
    background-color: #fff;
    position: absolute;
  }
  span::before {
    bottom: 0.5rem;
  }
  span::after {
    top: 0.5rem;
  }
  &.show {
    span {
      background-color: rgba(255, 255, 255, 0);
      &::before {
        bottom: 0;
        transform: rotate(45deg);
      }
      &::after {
        top: 0;
        transform: rotate(-45deg);
      }
    }
    &~ .menu-content-back {
      opacity: 0.5;
      z-index: 31;
    }
    &~ .menu-content {
      left: 0;
    }
  }
}

.menu-content-back {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  opacity: 0;
  z-index: -1;
  background-color: #000;
  transition: all 0.5s;
}
.menu-content {
  width: 20rem;
  height: 100%;
  position: fixed;
  top: 0;
  left: -100%;
  opacity: 1;
  z-index: 31;
  background-color: #22405F;
  transition: all 0.5s;
  ul.menu-list {
    margin-top: 3rem;
    padding: 0 0.5rem 0;
    li {
      display: block;
      list-style: none;
      line-height: 200%;
      border-bottom: solid 1px #fff;
      transition: background-color .5s;
      &:hover {
        background-color: #294664;
        transition: background-color .5s;
      }
      svg {
        display: inline-block;
        margin-right: .5rem;
        padding: 0 0 0.25rem 0;
        vertical-align: middle;
      }
      a {
        display: inline-block;
        position: relative;
        box-sizing: border-box;
        padding: 0.5rem 0.5rem 0.75rem 0;
        width: 100%;
        font-size: 1rem;
        color: #ffffff;
        text-decoration: none;
        &::before {
          content: "";
          position: absolute;
          right: 1rem;
          top: 1rem;
          width: 0.5rem;
          height: 0.5rem;
          border-right: solid 2px #fff;
          border-top: solid 2px #fff;
          transform: rotate(45deg);
        }
      }
    }
  }
}

</style>
